/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { normalize } from 'normalizr';

import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import { selectSkillById } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents,
  updatedObjectEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import * as segment from '@ibm-watson/shared/modules/ia/util/trackers/track-when-ready';
import { ReactivateSession } from '../../../../ts/session';
import { Language } from '../../../../ts/skill';
import {
  AppDispatch,
  CreateAsyncThunk,
  Entity,
  SystemEntity
} from '../../../../ts/state';
import {
  isActionsDigressionReturnConfirmationEnabled,
  selectIsAccessUpdatesEnabled
} from '../../../ia/store/features/selectors';
import { isCacheExpired } from '../../../ia/store/helper';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as api from '../../api/entities/entities';
import * as valueAPI from '../../api/entities/values/values';
import { ENTITY_TYPES, getSyntheticEntities } from '../../util/actionUtil';
import { getAction } from '../actions/selectors';
import { fetchSystemEntities } from '../system-entities/actions';
import { getSys } from '../system-entities/selectors';
import * as actionTypes from './actionTypes';
import { entitiesSchema, entitySchema } from './schema';
import { getById, getEntities, selectAreEntitiesLoading } from './selectors';

const buildModelObject = (operation, items) => {
  return {
    payload: { operation },
    response: {
      entities: {
        entities: items
      }
    }
  };
};

// --------------------------------------------- //
export const fetchEntitiesStart = skillId => ({
  type: actionTypes.FETCH_ENTITIES_START,
  skillId
});

export const fetchEntitiesSuccess = response => ({
  type: actionTypes.FETCH_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const fetchEntitiesError = error => ({
  type: actionTypes.FETCH_ENTITIES_ERROR,
  transactionId: error.transactionId,
  error
});

export const fetchEntities =
  (
    skillId,
    options,
    includeSystemEntities,
    appendSyntheticEntities,
    reactivateSession?
  ) =>
  async (dispatch, getState) => {
    dispatch(fetchEntitiesStart(skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to list
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await api.listUserEntities(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        options,
        undefined,
        reactivateSession
      );
      let { entities } = response;
      if (includeSystemEntities) {
        await dispatch(fetchSystemEntities(skillId, reactivateSession));
        entities = [
          ...entities,
          ..._.cloneDeep(getSys(getState()).filter(entity => !!entity.source))
        ];
      }
      const language =
        selectSkillById(getState(), skillId)?.language ?? Language.English;
      const sysYesNo = entities.find(
        entity => entity.entity === ENTITY_TYPES.confirmation.id
      );
      if (sysYesNo) {
        (sysYesNo as SystemEntity).language = language;
      }
      if (appendSyntheticEntities) {
        //there are certain 'system variables' that behave like options
        //we append these 'synthetic entities' to the list of _actual_ entities
        //so that condition builder and other components can present the list
        //of options easily.

        // trigger words are present ONLY if run_always is present
        const runAlwaysActionPresent =
          getAction(getState(), 'run_always') !== null;
        const digressionEnabled = isActionsDigressionReturnConfirmationEnabled(
          getState()
        );
        entities = [
          ...entities,
          ...getSyntheticEntities(digressionEnabled, runAlwaysActionPresent)
        ];
      }
      const normalizedResult = normalize(entities, entitiesSchema);

      dispatch(fetchEntitiesSuccess(normalizedResult));
      return {
        entities: normalizedResult,
        nextURL: response.nextURL,
        total: response.total
      };
    } catch (error) {
      dispatch(fetchEntitiesError(error));
      throw error;
    }
  };

interface FetchEntitiesIfRequiredParams {
  skillId: string;
  options: { page_limit: number };
  includeSystemEntities: boolean;
  appendSyntheticEntities?: boolean;
}

export const fetchEntitiesIfRequired = createAsyncThunk<
  Record<string, Entity> | any,
  FetchEntitiesIfRequiredParams,
  CreateAsyncThunk
>(
  'entities/fetchEntitiesIfRequired',
  async (
    { skillId, options, includeSystemEntities, appendSyntheticEntities },
    { dispatch, getState }
  ) => {
    const state = getState();
    if (
      !state.entities?.props?.lastFetched ||
      isCacheExpired(state.entities?.props?.lastFetched)
    ) {
      return await dispatch(
        fetchEntities(
          skillId,
          options,
          includeSystemEntities,
          appendSyntheticEntities,
          ReactivateSession.NEVER
        )
      );
    }
    return getEntities(state);
  },
  {
    condition: (_, { getState }) => {
      const isLoading = selectAreEntitiesLoading(getState());
      return !isLoading;
    }
  }
);

// --------------------------------------------- //

export const fetchMoreEntitiesStart = nextURL => ({
  type: actionTypes.FETCH_MORE_ENTITIES_START,
  nextURL
});

export const fetchMoreEntitiesSuccess = response => {
  return {
    type: actionTypes.FETCH_MORE_ENTITIES_SUCCESS,
    response,
    payload: { operation: operations.UPDATE }
  };
};

export const fetchMoreEntitiesError = error => ({
  type: actionTypes.FETCH_MORE_ENTITIES_ERROR,
  error
});

export const fetchMoreEntities = (nextURL, params) => async dispatch => {
  dispatch(fetchMoreEntitiesStart(nextURL));

  try {
    const response = await api.listMoreEntities(nextURL, params);
    const normalizedResult = normalize(response.entities, entitiesSchema);
    dispatch(fetchMoreEntitiesSuccess(normalizedResult));
    return {
      entities: normalizedResult,
      nextURL: response.nextURL
    };
  } catch (error) {
    dispatch(fetchMoreEntitiesError(error));
    throw error;
  }
};

// --------------------------------------------- //
export const createEntityStart = (skillId, entity) => ({
  type: actionTypes.CREATE_ENTITY_START,
  skillId,
  entity
});

export const createEntitySuccess = response => ({
  type: actionTypes.CREATE_ENTITY_SUCCESS,
  response,
  payload: { operation: operations.CREATE }
});

export const createEntityError = error => ({
  type: actionTypes.CREATE_ENTITY_ERROR,
  transactionId: error.transactionId,
  error
});

export const createEntity =
  (skillId: string, entity: Entity, eventSource) =>
  async (dispatch, getState) => {
    dispatch(createEntityStart(skillId, entity));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await api.create(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity
      );
      if (
        entity.entity.startsWith('sys-') ||
        entity.entity.startsWith('sys_')
      ) {
        entity.source = 'system.entities';
        // we need to merge the existing system entity with the one returned from the api call
        // the returned sys entity does not have a description
        const sysEntity = getById(getState(), entity.entity);
        const merged = { ...sysEntity, ...entity };
        dispatch(createEntitySuccess(normalize(merged, entitySchema)));
      } else {
        if (!response?.values) {
          response.values = [];
        }
        response.created = new Date().toISOString();
        dispatch(createEntitySuccess(normalize(response, entitySchema)));
      }
      createdObjectEvents.entity({
        object: skillId ? `${skillId}--${entity.entity}` : entity.entity,
        parentPageName: eventSource,
        resultValue: 'success',
        successFlag: true
      });
      return response;
    } catch (error) {
      dispatch(createEntityError(error));
      createdObjectEvents.entity({
        object: null,
        parentPageName: eventSource,
        resultValue: getErrorString(error) || error,
        successFlag: false
      });
      throw error;
    }
  };

// --------------------------------------------- //
export const createValueStart = (skillId, entity, value) => ({
  type: actionTypes.CREATE_VALUE_START,
  skillId,
  entity,
  value
});

export const createValueSuccess = response => ({
  type: actionTypes.CREATE_VALUE_SUCCESS,
  response,
  payload: { operation: operations.UPDATE }
});

export const createValueError = error => ({
  type: actionTypes.CREATE_VALUE_ERROR,
  transactionId: error.transactionId,
  error
});

export const createValue =
  (skillId: string, entity: any, value: any, eventSource: any) =>
  async (dispatch, getState) => {
    dispatch(createValueStart(skillId, entity, value));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to create
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await valueAPI.create(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity.entity,
        value
      );
      response[response.type] = value[response.type];
      const copiedEntity = { ...entity, values: [...entity.values, response] }; // don't bother redux with unfortunate mutations
      copiedEntity.updated = new Date().toISOString();
      dispatch(createValueSuccess(normalize(copiedEntity, entitySchema)));

      if (value.type === 'synonyms') {
        createdObjectEvents.entityValue(
          {
            object: skillId
              ? `${skillId}--${entity.entity}--${value.value}`
              : `${entity.entity}--${value}`,
            category: 'synonym',
            parentPageName: eventSource,
            resultValue: 'success',
            successFlag: true
          },
          {
            waValueCount: _.get(value, 'synonyms.length')
          }
        );
      } else if (value.type === 'patterns') {
        createdObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${value.value}`
            : `${entity.entity}--${value}`,
          category: 'pattern',
          parentPageName: eventSource,
          resultValue: 'success',
          successFlag: true
        });
      } else {
        createdObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${value.value}`
            : `${entity.entity}--${value}`,
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: 'success',
          successFlag: true
        });
      }
      return copiedEntity;
    } catch (error) {
      dispatch(createValueError(error));

      if (value.type === 'synonyms') {
        createdObjectEvents.entityValue({
          object: null, //there is no object in the failed case.
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      } else if (value.type === 'patterns') {
        createdObjectEvents.entityValue({
          object: null, //there is no object in the failed case.
          category: 'pattern',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      } else {
        createdObjectEvents.entityValue({
          object: null, //there is no object in the failed case.
          category: 'pattern',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      }
      throw error;
    }
  };

// --------------------------------------------- //
export const updateEntityStart = (skillId, entity, updatedEntity) => ({
  type: actionTypes.UPDATE_ENTITY_START,
  skillId,
  entity,
  updatedEntity
});

export const updateEntitySuccess = (response, previousEntityId) => ({
  type: actionTypes.UPDATE_ENTITY_SUCCESS,
  response,
  previousEntityId,
  payload: { operation: operations.UPDATE }
});

export const updateEntityError = error => ({
  type: actionTypes.UPDATE_ENTITY_ERROR,
  transactionId: error.transactionId,
  error
});

export const updateEntity =
  (skillId, entity, updatedEntity, eventSource) =>
  async (dispatch, getState) => {
    dispatch(updateEntityStart(skillId, entity, updatedEntity));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      await api.update(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity.entity,
        updatedEntity
      );
      // passing a 2nd-parameter denotes a renaming operation has occurred (as opposed to a mutating operation)
      const previousEntityId =
        entity.entity === updatedEntity.entity ? null : entity.entity;
      updatedEntity.updated = new Date().toISOString();
      const normalized = normalize(updatedEntity, entitySchema);
      dispatch(updateEntitySuccess(normalized, previousEntityId));

      updatedObjectEvents.entity({
        object: skillId
          ? `${skillId}--${normalized.result}`
          : normalized.result,
        parentPageName: eventSource,
        resultValue: 'success',
        successFlag: true
      });
      return updatedEntity;
    } catch (error) {
      dispatch(updateEntityError(error));
      updatedObjectEvents.entity({
        object: skillId ? `${skillId}--${entity.entity}` : entity.entity,
        parentPageName: eventSource,
        resultValue: getErrorString(error) || error,
        successFlag: false
      });
      throw error;
    }
  };

// --------------------------------------------- //
export const updateValueStart = (
  skillId,
  entity,
  currentValue,
  updatedValue
) => ({
  type: actionTypes.UPDATE_VALUE_START,
  skillId,
  entity,
  currentValue,
  updatedValue
});

export const updateValueSuccess = response => ({
  type: actionTypes.UPDATE_VALUE_SUCCESS,
  response,
  payload: { operation: operations.UPDATE }
});

export const updateValueError = error => ({
  type: actionTypes.UPDATE_VALUE_ERROR,
  transactionId: error.transactionId,
  error
});

export const updateValue =
  (
    skillId: string,
    entity: any,
    currentValue: any,
    updatedValue: any,
    eventSource: any
  ) =>
  async (dispatch, getState) => {
    dispatch(updateValueStart(skillId, entity, currentValue, updatedValue));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to update
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await valueAPI.update(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity.entity,
        currentValue.value,
        updatedValue
      );
      const copiedValues = entity.values.filter(
        value => value.value !== currentValue.value
      );
      entity.updated = new Date().toISOString();
      const copiedEntity = { ...entity, values: [...copiedValues, response] };
      dispatch(updateValueSuccess(normalize(copiedEntity, entitySchema)));

      if (updatedValue.type === 'synonyms') {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: 'success',
          successFlag: true
        });
      } else if (updatedValue.type === 'patterns') {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'pattern',
          parentPageName: eventSource,
          resultValue: 'success',
          successFlag: true
        });
      } else {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: 'success',
          successFlag: true
        });
      }

      return copiedEntity;
    } catch (error) {
      dispatch(updateValueError(error));

      if (updatedValue.type === 'synonyms') {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      } else if (updatedValue.type === 'patterns') {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'pattern',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      } else {
        updatedObjectEvents.entityValue({
          object: skillId
            ? `${skillId}--${entity.entity}--${updatedValue.value}`
            : updatedValue.value,
          category: 'synonym',
          parentPageName: eventSource,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      }

      throw error;
    }
  };

// --------------------------------------------- //
export const deleteEntityStart = () => ({
  type: actionTypes.DELETE_ENTITY_START
});

export const deleteEntitySuccess = entity => ({
  type: actionTypes.DELETE_ENTITY_SUCCESS,
  entity,
  payload: { operation: operations.DELETE }
});

export const deleteEntityError = error => ({
  type: actionTypes.DELETE_ENTITY_ERROR,
  transactionId: error.transactionId,
  error
});

export const deleteEntity = (skillId, entity) => async (dispatch, getState) => {
  dispatch(deleteEntityStart());
  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to deleteEntity
  const assistantId = getAssistantId(getState());
  const isNewIA = selectIsNewIa(getState());
  const useNewIAUpdatedAccess =
    isNewIA && selectIsAccessUpdatesEnabled(getState());
  try {
    const response = await api.deleteEntity(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      entity
    );
    dispatch(deleteEntitySuccess(entity));
    deletedObjectEvents.entity({
      object: `${skillId}--${entity.entity}`,
      resultValue: 'success',
      successFlag: true
    });
    return response;
  } catch (error) {
    dispatch(deleteEntityError(error));
    deletedObjectEvents.entity({
      object: `${skillId}--${entity.entity}`,
      resultValue: getErrorString(error) || error,
      successFlag: false
    });
    throw error;
  }
};

// --------------------------------------------- //

export const deleteEntitiesStart = () => ({
  type: actionTypes.DELETE_ENTITIES_START
});

export const deleteEntitiesSuccess = entities => ({
  type: actionTypes.DELETE_ENTITIES_SUCCESS,
  entities: entities.map(entity => entity.entity),
  ...buildModelObject(
    operations.DELETE,
    entities.reduce((accumulator, currentValue) => {
      accumulator[currentValue.entity] = currentValue;
      return accumulator;
    }, {})
  )
});

export const deleteEntitiesError = error => ({
  type: actionTypes.DELETE_ENTITIES_ERROR,
  error
});

export const deleteEntities =
  (skillId: string, entities: Entity[], deleteAll = false) =>
  async (dispatch: AppDispatch, getState) => {
    dispatch(deleteEntitiesStart());
    let deletedEntities = entities;
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to list/delete
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      if (deleteAll) {
        // when deleting all entities, we need to first get all entities
        // then use all entities id and make a PATCH call to delete all
        const response = await api.listUserEntities(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          {},
          false
        );
        deletedEntities = response.entities;
      }
      if (deletedEntities.length === 1) {
        // when we only have one entity to delete,
        // we should make a call to `DELETE /entities/{entity_id}`
        // instead of making a PATCH call.
        await api.deleteEntity(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          deletedEntities[0]
        );
      } else {
        await api.deleteEntities(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          deletedEntities
        );
      }
      dispatch(deleteEntitiesSuccess(deletedEntities));
      if (deletedEntities.length > 1) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Entities',
          true,
          null,
          'DeleteSome',
          'true'
        );
      } else {
        deletedObjectEvents.entity({
          object: skillId
            ? `${skillId}--${deletedEntities[0]?.entity}`
            : deletedEntities[0]?.entity,
          resultValue: 'success',
          successFlag: true
        });
      }

      return entities;
    } catch (error) {
      dispatch(deleteEntitiesError(error));
      if (deletedEntities.length > 1) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Entities',
          false,
          null,
          'DeleteSome',
          'false'
        );
      } else {
        deletedObjectEvents.entity({
          object: skillId
            ? `${skillId}--${deletedEntities[0]?.entity}`
            : deletedEntities[0]?.entity,
          resultValue: getErrorString(error) || error,
          successFlag: false
        });
      }
      throw error;
    }
  };

// --------------------------------------------- //

export const deleteAllEntitiesStart = () => ({
  type: actionTypes.DELETE_ALL_ENTITIES_START
});

export const deleteAllEntitiesSuccess = response => ({
  type: actionTypes.DELETE_ALL_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.DELETE }
});

export const deleteAllEntitiesError = error => ({
  type: actionTypes.DELETE_ALL_ENTITIES_ERROR,
  transactionId: error.transactionId,
  error
});

export const deleteAllEntities = skillId => async (dispatch, getState) => {
  dispatch(deleteAllEntitiesStart());
  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to deleteAllEntities
  const assistantId = getAssistantId(getState());
  const isNewIA = selectIsNewIa(getState());
  const useNewIAUpdatedAccess =
    isNewIA && selectIsAccessUpdatesEnabled(getState());
  try {
    await api.deleteAllEntities(skillId, useNewIAUpdatedAccess, assistantId);
    dispatch(deleteAllEntitiesSuccess([]));
    segment.customEvent(
      'BulkDeleted',
      skillId,
      'Entities',
      true,
      null,
      'DeleteAll',
      'true'
    );
    return [];
  } catch (error) {
    dispatch(deleteAllEntitiesError(error));
    segment.customEvent(
      'BulkDeleted',
      skillId,
      'Entities',
      false,
      null,
      'DeleteAll',
      'false'
    );
    throw error;
  }
};

// --------------------------------------------- //

export const replaceEntitiesStart = () => ({
  type: actionTypes.REPLACE_ENTITIES_START
});

export const replaceEntitiesSuccess = response => ({
  type: actionTypes.REPLACE_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const replaceEntitiesError = error => ({
  type: actionTypes.REPLACE_ENTITIES_ERROR,
  transactionId: error.transactionId,
  error
});

export const replaceEntities =
  (skillId, entitiesToKeep, systemEntities) => async (dispatch, getState) => {
    dispatch(replaceEntitiesStart());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to list, replace
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      await api.replaceEntities(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entitiesToKeep,
        systemEntities
      );
      try {
        const response = await api.list(
          skillId,
          useNewIAUpdatedAccess,
          assistantId
        );
        const normalizedResult = normalize(response.entities, entitiesSchema);
        dispatch(replaceEntitiesSuccess(normalizedResult));

        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Entities',
          true,
          null,
          'InvertDelete',
          'true'
        );

        return {
          entities: normalizedResult,
          nextURL: response.nextURL,
          total: response.total
        };
      } catch (error) {
        dispatch(replaceEntitiesError(error));
        throw error;
      }
    } catch (error) {
      dispatch(replaceEntitiesError(error));
      segment.customEvent(
        'BulkDeleted',
        skillId,
        'Entities',
        false,
        null,
        'InvertDelete',
        'false'
      );

      throw error;
    }
  };

export const importEntitiesStart = (skillId, file) => ({
  type: actionTypes.IMPORT_ENTITIES_START,
  skillId,
  file
});

export const importEntitiesSuccess = response => ({
  type: actionTypes.IMPORT_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const importEntitiesError = error => ({
  type: actionTypes.IMPORT_ENTITIES_ERROR,
  error
});

export const importEntities = (skillId, file) => async (dispatch, getState) => {
  dispatch(importEntitiesStart(skillId, file));
  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to importEnts, list
  const assistantId = getAssistantId(getState());
  const isNewIA = selectIsNewIa(getState());
  const useNewIAUpdatedAccess =
    isNewIA && selectIsAccessUpdatesEnabled(getState());
  try {
    let jobLocation = await api.importEntities(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      file
    );
    let jobInfo,
      counter = 0;
    // should remove `/instances/:instanceId` from the url
    // because we don't want to apply access control if the access control is not on
    // in one cloud url world, there will always be `/instances/:instanceId`
    // we have to remove it conditionally
    // for example, `/instances/5522ca66-8b4d-4438-9afa-dc47089fddda/v2/skills` -> `/v2/skills`
    // if we need the uoa endpoint, we will add it back on server side.
    // eslint-disable-next-line no-useless-escape
    jobLocation = jobLocation.replace(/\/instances\/[^\/]+\//i, '/');
    const throttledRequest = _.throttle(
      (resolve, reject) =>
        api.getImportEntitiesJobInfo(jobLocation).then(resolve).catch(reject),
      1000
    ); // throttle the call for 1 second so that it is at most called once per second
    do {
      jobInfo = await new Promise((resolve, reject) =>
        throttledRequest(resolve, reject)
      );
      counter++;
    } while (!jobInfo.finished && counter < 60);
    if (!jobInfo.finished && counter >= 60) {
      throw new Error('Import taking too long.');
    }
    const response = await api.list(
      skillId,
      useNewIAUpdatedAccess,
      assistantId
    );
    const normalizedResult = normalize(response.entities, entitiesSchema);
    dispatch(importEntitiesSuccess(normalizedResult));
    return {
      entities: normalizedResult,
      nextURL: response.nextURL,
      total: response.total,
      counts: _.get(jobInfo, 'data', {})
    };
  } catch (error) {
    dispatch(importEntitiesError(error));
    throw error;
  }
};

export const filterEntitiesStart = (skillId, queryString) => ({
  type: actionTypes.FILTER_ENTITIES_START,
  queryString,
  skillId
});

export const filterEntitiesSuccess = response => ({
  type: actionTypes.FILTER_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const filterEntitiesError = error => ({
  type: actionTypes.FILTER_ENTITIES_ERROR,
  error
});

export const filterEntities =
  (skillId, queryString, allSystemEntities) => async (dispatch, getState) => {
    dispatch(filterEntitiesStart(skillId, queryString));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to filterEntities
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const options = {
        export: true,
        page_limit: 100000,
        include_audit: true
      };

      const response: any = await api.filterEntities(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        options,
        queryString,
        allSystemEntities
      );
      const normalizedResult = normalize(response.entities, entitiesSchema);
      dispatch(filterEntitiesSuccess(normalizedResult));
      return {
        entities: normalizedResult
      };
    } catch (error) {
      dispatch(filterEntitiesError(error));
      throw error;
    }
  };

// --------------------------------------------- //
export const deleteEntityValuesStart = () => ({
  type: actionTypes.DELETE_ENTITY_VALUES_START
});

export const deleteEntityValuesSuccess = response => ({
  type: actionTypes.DELETE_ENTITY_VALUES_SUCCESS,
  response,
  payload: { operation: operations.UPDATE }
  ///payload : { operation : operations.DELETE }
});

export const deleteEntityValuesError = error => ({
  type: actionTypes.DELETE_ENTITY_VALUES_ERROR,
  error
});

export const deleteEntityValues =
  (skillId, entity, values) => async (dispatch, getState) => {
    dispatch(deleteEntityValuesStart());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const state = getState();
    const assistantId = getAssistantId(state);
    const isNewIA = selectIsNewIa(state);
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(state);
    try {
      await api.deleteEntityValues(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity.entity,
        values
      );
      const removedVals = values.map(v => v.value);
      const copiedValues = entity.values.filter(value => {
        return removedVals.indexOf(value.value) === -1;
      });
      entity.updated = new Date().toISOString();
      const copiedEntity = { ...entity, values: copiedValues };
      //const copiedEntity = {...entity, values : [...copiedValues, response] };
      dispatch(
        deleteEntityValuesSuccess(normalize(copiedEntity, entitySchema))
      );

      if (values.length === entity.values.length) {
        // delete all
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Values',
          true,
          null,
          'DeleteAll',
          'true'
        );
      } else if (values.length > 1 && values.length !== entity.values.length) {
        // bulk delete some
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Values',
          true,
          null,
          'DeleteSome',
          'true'
        );
      } else {
        // single delete
        if (values[0] && values[0].type === 'patterns') {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            category: 'pattern',
            resultValue: 'success',
            successFlag: true
          });
        } else if (values[0] && values[0].type === 'synonyms') {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            category: 'synonym',
            resultValue: 'success',
            successFlag: true
          });
        } else {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            resultValue: 'success',
            successFlag: true
          });
        }
      }

      return copiedEntity;
    } catch (error) {
      dispatch(deleteEntityValuesError(error));

      if (values.length === entity.values.length) {
        // delete all
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Values',
          false,
          null,
          'DeleteAll',
          'false'
        );
      } else if (values.length > 1 && values.length !== entity.values.length) {
        // bulk delete some
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Values',
          false,
          null,
          'DeleteSome',
          'false'
        );
      } else {
        // single delete
        if (values[0] && values[0].type === 'patterns') {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            category: 'pattern',
            resultValue: getErrorString(error) || error,
            successFlag: false
          });
        } else if (values[0] && values[0].type === 'synonyms') {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            category: 'synonym',
            resultValue: getErrorString(error) || error,
            successFlag: false
          });
        } else {
          deletedObjectEvents.entityValue({
            object: skillId
              ? `${skillId}--${entity.entity}--${values[0]?.value}`
              : values[0]?.value,
            resultValue: getErrorString(error) || error,
            successFlag: false
          });
        }
      }
      throw error;
    }
  };
